import React, { useEffect, useState } from 'react';

import { useSelector } from 'react-redux';

import useStyles from './useStyles';
import selectSessionToken from '../../selectors/sessionToken';

interface FileViewerProps {
  filePath: string;
  fileName: string;
  onClose: () => void;
}

const FileViewer: React.FC<FileViewerProps> = ({ filePath, fileName, onClose }) => {
  const [fileUrl, setFileUrl] = useState<string>('');
  const [fileType, setFileType] = useState<string>('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const sessionToken = useSelector(selectSessionToken);
  const classes = useStyles();

  const handleClose = () => {
    // Once the url is closed, revoke the object URL to free up memory
    if (fileUrl) {
      URL.revokeObjectURL(fileUrl);
      setFileUrl('');
    }
    onClose();
  };
  
  const handleDownload = () => {
    if (!fileUrl) return;
    let parsed;
    try {
      parsed = new URL(fileUrl);
      if (parsed.protocol !== 'blob:') return;
    } catch {
      return;
    } //checking only blob urls are present
  const a = document.createElement('a');
  a.setAttribute('href', fileUrl);
  a.setAttribute('download', fileName?.replace(/[<>:"|?*]/g, '_') || 'download'); //adding the file name after sanitizing
  document.body.appendChild(a);
  a.click();
  a.remove();

  };

  useEffect(() => {
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';

    if (!filePath || !sessionToken) {
      setError('Missing file path or authentication token');
      setLoading(false);
      return;
    }

    // Build URL with query parameters including auth token
    const params = new URLSearchParams({
      path: filePath     
    });

    // Fetching the File from the mounted Path via view/file-storage endpoint
    fetch(`/view/file-storage?${params.toString()}`, {
      headers: {
        Authorization: `Bearer ${sessionToken}`,
      },
    })
      .then(async res => {
        if (res.status === 401) {
          setError('Unauthorized');
          return null;
        }
        
        if (!res.ok) {
          throw new Error(`Failed to fetch file: ${res.statusText}`);
        }
        
        // If successful, create a blob URL for the file
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        setFileUrl(url);
        setFileType(blob.type);
        setLoading(false);
      })      
      .catch((err) => {
        console.error('Error fetching file:', err);
        setError('Error fetching file.');   
        setLoading(false);    
      });
      return () => {
      // Restore body scroll when modal closes
      document.body.style.overflow = '';
      if (fileUrl) {
        URL.revokeObjectURL(fileUrl);
      }
    };
  }, [filePath, sessionToken]);
  

  return (
    <div className={classes.modalOverlay}>
      {/* Header */}
      <div className={classes.header}>
        <h3 className={classes.title}>{fileName}</h3>
        <div>
          {/* NEW: Download button */}
          {fileUrl && !error && (
            <button
              onClick={handleDownload}
              className={classes.downloadButton}
            >
              Download
            </button>
          )}
          <button
            onClick={handleClose}
            className={classes.closeButton}
          >
            Close
          </button>
        </div>
      </div>
      {/* Content */}
      <div className={classes.content}>   
        {loading && (
          <div className={classes.loadingFont}>
            <div className={classes.loadingIcon}>⏳</div>Loading file...
          </div>
        )}
        {error && (
          <div className={classes.error}>⚠️ {error}</div>
        )}
        {/* To preview text files */}
        {fileUrl && !error && !loading && (
          fileType?.startsWith('text/') ? (
            <embed
              src={fileUrl}
              type={fileType}
              className={classes.textFileType}
              aria-label={fileName}
            />
          ) : (
            <object
              data={fileUrl}
              type={fileType || 'application/octet-stream'} /* To preview Other files including pdf, jpg etc */
              className={classes.otherFileType}
              aria-label={fileName}
            >
              <div className={classes.fallbackStyle}>
                <p className={classes.previewUnableText}>Unable to preview this file directly in the browser, but you can download it.</p>
                <p className={classes.previewUnableFileName}> File type: {fileType || 'unknown'}</p>
              </div>
            </object>
          )        
        ) }
      </div>
    </div>
  );
};

export default FileViewer;
